import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle } from "lucide-react";
import Header from "@/components/Header";

const FAQ = () => {
  const faqData = [
    {
      category: "Membership",
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          question: "Who is eligible to join Natcon Staff Sacco?",
          answer: "Membership is exclusively for employees of the National Construction Authority. All NCA employees are welcome to join our growing community of members."
        },
        {
          question: "How do I join the Sacco?",
          answer: "Joining is simple! Complete our online application form available on our website. Once submitted, our team will review your application and guide you through the next steps."
        },
        {
          question: "What are the initial costs to become a member?",
          answer: "To become a member, you need to pay a one-time joining fee of Kshs. 1,500 and purchase a minimum share capital of Kshs. 5,000 (which equals 250 shares at Kshs. 20 each). This totals Kshs. 6,500 to get started."
        }
      ]
    },
    {
      category: "Your Finances",
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          question: "What is the difference between Monthly Deposits and Share Capital?",
          answer: "Monthly Deposits are your non-withdrawable savings contributions that build your savings balance. Share Capital represents your ownership/equity in the Sacco. While deposits can be refunded when you leave, share capital is non-refundable but transferable."
        },
        {
          question: "Can I withdraw my monthly deposits?",
          answer: "No, monthly deposits cannot be partially withdrawn while you remain a member. They can only be refunded in full when you completely leave the Sacco. This policy helps ensure the Sacco's stability and your long-term savings growth."
        },
        {
          question: "Can I get a refund for my Share Capital if I leave the Sacco?",
          answer: "Share Capital is non-refundable, as it represents your ownership stake in the Sacco. However, when you leave, you can transfer your share capital to another person who meets the membership requirements."
        },
        {
          question: "How do I earn returns on my savings?",
          answer: "You earn returns in two ways: interest on your monthly deposits based on the Sacco's performance, and dividends on your share capital. Both are distributed based on our annual financial performance and are declared at the Annual General Meeting."
        }
      ]
    },
    {
      category: "Loans",
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          question: "When can I become eligible for a loan?",
          answer: "You become eligible for a loan after being a member for six (6) months. This waiting period allows you to build your savings and demonstrates your commitment to the Sacco."
        },
        {
          question: "Are there any exceptions to the six-month waiting period for loans?",
          answer: "Yes! If you transfer your membership directly from another Sacco, you can qualify for a loan immediately upon joining Natcon Sacco. This recognizes your prior Sacco experience and commitment."
        },
        {
          question: "What is the maximum loan amount I can apply for?",
          answer: "For Emergency Loans, the maximum is Kshs. 100,000. For normal loans (Development and School Fees Loans), you can borrow up to three (3) times your total deposits. The more you save, the more you can borrow!"
        },
        {
          question: "How are loans secured?",
          answer: "All loans must be secured by guarantors who are fellow Sacco members. The total deposits of your guarantors must equal or exceed the loan amount you're applying for. This system protects both you and the Sacco."
        },
        {
          question: "How long does it take to get a loan processed?",
          answer: "Once all requirements are met (application completed, guarantors secured, all documents submitted), your loan will be processed within two (2) working days. We pride ourselves on quick, efficient service!"
        }
      ]
    },
    {
      category: "Guarantors",
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          question: "What are the requirements to be a guarantor for a loan?",
          answer: "To serve as a guarantor, a member must: be in good standing with the Sacco, have sufficient savings to cover their guarantee amount, not be over-guaranteed on other loans, and be willing and able to repay the loan if the borrower defaults. This ensures the security of all loans."
        }
      ]
    },
    {
      category: "The SACCO Portal & Governance",
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          question: "How can I access my Sacco account online?",
          answer: "You can access our services at www.natconsacco.or.ke and log into the dedicated member portal at https://selfservice.natconsacco.or.ke/. These platforms provide 24/7 access to your account information."
        },
        {
          question: "What can I do on the online member portal?",
          answer: "The member portal is your comprehensive account dashboard. You can view your personal information, check savings and loan statements, review dividend statements, apply for loans, use our loan calculator, see your guarantee details, and access important documents like our By-Laws."
        },
        {
          question: "Who manages the Sacco?",
          answer: "Natcon Sacco is democratically managed by a Board of Directors, which includes both a Management Committee and a Supervisory Committee. These leaders are elected by members at our Annual General Meeting (AGM). Day-to-day operations are handled by our professional Sacco Secretariat."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Frequently Asked Questions (FAQ)
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90 leading-relaxed">
              Have questions? We have answers. We've compiled a list of the most common questions we receive about joining Natcon Sacco, managing your account, applying for loans, and more. If you can't find your answer here, please don't hesitate to contact us.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Accordion type="multiple" className="space-y-6">
              {faqData.map((category, categoryIndex) => (
                <Card key={categoryIndex} className="border-2 border-border/50 shadow-lg">
                  <AccordionItem value={`category-${categoryIndex}`} className="border-none">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          {category.icon}
                        </div>
                        <h2 className="text-xl font-semibold text-foreground">
                          {category.category}
                        </h2>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-6 mt-4">
                        {category.questions.map((qa, questionIndex) => (
                          <div key={questionIndex} className="border-l-4 border-primary/30 pl-4">
                            <h3 className="text-lg font-medium text-foreground mb-2">
                              {qa.question}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                              {qa.answer}
                            </p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Card>
              ))}
            </Accordion>

            {/* Contact CTA */}
            <Card className="mt-12 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-muted-foreground mb-6 text-lg">
                  If you couldn't find the answer you were looking for, our member support team is ready to help.
                </p>
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <Link to="/contact">
                    Contact Us
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;